import { Cell } from "./cell"
import { calcActualColWidth, createColumn } from "./column"
import Column from "./column/default"
import { Rect } from "./common"
import Row, { PublicRow } from "./row"
import {
	CellRenderer,
	RowSelectBoundRenderer,
	MultiRowSelectBoundRenderer,
	RowColSelectBoundRenderer
} from "./bound-renderer"
import { isMobile } from "../utils"

/**
 * @typedef {Object} Rect
 * @property {number} left
 * @property {number} top
 * @property {number} width
 * @property {number} height
 */

/**
 * @typedef {Object} CurrentCellInfo
 * @property {any} value
 * @property {Column} col
 * @property {number} rowIndex
 * @property {any} rowId
 */

/**
 * キャンバスへのグリッドの描画を行うクラス
 */
export default class Grid {
	/**
	 * @type {import("./common").Context} コンテキスト
	 */
	#ctx

	get ctx () {
		return this.#ctx
	}

	/**
	 * @type {Rect} 最後にレンダリングした矩形
	 */
	lastRenderedRect

	/**
	 * @type {number} デフォルトのカラム幅
	 */
	defaultColWidth = 200
	/**
	 * @type {number} デフォルトのカラム最小幅
	 */
	defaultColMinWidth = 100

	/**
	 * @type {number} ヘッダ高さ
	 */
	get headerHeight () {
		return this.ctx.headerHeight
	}

	/**
	 * @type {number} 行高さ
	 */
	get rowHeight () {
		return this.#ctx.rowHeight
	}

	/**
	 * @type {boolean} 編集中か
	 */
	#isModifying = false

	/**
	 * 表示カラムフィルタ
	 * @param {Column} col
	 */
	static visibleColFilter = col => !col.hidden

	/**
	 * @type {Row[]} ソート、フィルター適用後の行配列
	 */
	get rows () {
		return this.ctx.rows
	}

	/**
	 * @type {Row[]} ソート、フィルター適用前の行配列
	 */
	originalRows = []

	/**
	 * @type {Column[]} 非表示を含む列
	 */
	#cols = []

	/**
	 * @type {Column[]} 表示列
	 */
	get cols () {
		return this.#cols.filter(Grid.visibleColFilter)
	}

	/**
	 * @type {Column[]} 非表示を含む固定列
	 */
	#fixedCols = []

	/**
	 * @type {Column[]} 表示固定列
	 */
	get fixedCols () {
		return this.#fixedCols.filter(Grid.visibleColFilter)
	}

	/**
	 * @type {Column[]} ヘッダ描画用の非表示を含む固定列
	 */
	#fixedColsForHeader = []

	/**
	 * @type {Column[]} ヘッダ描画用の固定列
	 */
	get fixedColsForHeader () {
		return this.#fixedColsForHeader.filter(Grid.visibleColFilter)
	}

	/**
	 * @type {Column[]} ヘッダ描画用の非表示を含む列
	 */
	#colsForHeader = []

	/**
	 * @type {Column[]} ヘッダ描画用の列
	 */
	get colsForHeader () {
		return this.#colsForHeader.filter(Grid.visibleColFilter)
	}

	/**
	 * @type {Column[]} すべての表示列
	 */
	get allCols () {
		return [...this.fixedCols, ...this.cols]
	}

	/**
	 * @type {Column[]} 非表示列も含んだすべての列
	 */
	get allColsWithUnvisible () {
		return [...this.#fixedCols, ...this.#cols]
	}

	/**
	 * @type {Column[]} 非表示列も含んだすべてのヘッダ描画用の列
	 */
	get allColsForHeader () {
		return [...this.#colsForHeader, ...this.#cols]
	}

	/**
	 * @type {Object.<string, Column>} 列IDと列オブジェクトのマッピング
	 */
	idToColMap = {}

	/**
	 * @type {Object.<string, Row>} 行IDと行オブジェクトのマッピング
	 */
	idToRowMap = {}

	/**
	 * @type {Row|null} 現在選択されている行
	 */
	currentRow

	/**
	 * @type {Row[]} 現在選択されている行
	 */
	selectedRowIndices = []

	/**
	 * @type {Colmun|null} 現在選択されている列
	 */
	currentCol

	/**
	 * @type {CurrentCellInfo} 選択セルの情報
	 */
	get currentCellInfo () {
		return this.ctx.currentCellInfo
	}

	#width
	/**
	 * @type {number} このグリッドの幅
	 */
	get width () {
		return this.#width
	}

	/**
	 * @type {number} このグリッドの高さ
	 */
	get height () {
		return this.rows.length * this.rowHeight + this.headerHeight
	}

	/**
	 * @type {import("./bound-renderer").BaseSelectBoundRenderer}
	 */
	selectBorderRenderer

	/**
	 * @type {string}
	 */
	rowIdField

	/**
	 * @type {() => any}
	 */
	autoInsert

	/**
	 * セル単位の情報
	 * key: rowId
	 * value: {coldId, cellinfo}[]
	 */
	cellInfoMap = {}

	#readonlyRowRule = () => false

	get readonlyRowRule () {
		return this.#readonlyRowRule
	}

	#modifiedRowIds = new Set()

	get modifiedRowIds () {
		return this.#modifiedRowIds
	}

	/**
	 * コンストラクタ
	 * @param {import("./common").Context} ctx
	 */
	constructor (ctx, { onSizeChanged, requestScroll, requestCursorChange, onFilterClicked }) {
		this.#ctx = ctx
		if (ctx.selectMode === "cell") {
			this.selectBorderRenderer = new CellRenderer(ctx, this)
		} else if (ctx.selectMode === "row") {
			this.selectBorderRenderer = new RowSelectBoundRenderer(ctx, this)
		} else if (ctx.selectMode === "multiRow") {
			this.selectBorderRenderer = new MultiRowSelectBoundRenderer(ctx, this)
		} else if (ctx.selectMode === "boundary") {
			this.selectBorderRenderer = new RowColSelectBoundRenderer(ctx, this)
		}
		this.onSizeChangedListener = onSizeChanged
		this.requestScroll = requestScroll
		this.requestCursorChange = requestCursorChange
		this.onFilterClicked = onFilterClicked
	}

	/**
	 * 列を設定する
	 * @param {any[]} colOptionsList
	 */
	setColumns (colOptionsList) {
		this.#cols = []
		this.#fixedCols = []
		this.#colsForHeader = []
		this.#fixedColsForHeader = []
		this.idToColMap = {}
		colOptionsList.forEach(colOptions => {
			const col = createColumn(colOptions)

			if (col.options.fixed) this.#fixedColsForHeader.push(col)
			else this.#colsForHeader.push(col)
			if (col.id) {
				this.idToColMap[col.id] = col
			}
		})

		const flattenCol = (acc, cur) => {
			if (cur.type === "merge") {
				acc = acc.concat(cur.childCols.reduce(flattenCol, []))
			} else {
				acc.push(cur)
				if (cur.id) {
					this.idToColMap[cur.id] = cur
				}
			}
			return acc
		}

		this.#cols = this.#colsForHeader.reduce(flattenCol, [])
		this.#fixedCols = this.#fixedColsForHeader.reduce(flattenCol, [])

		this.initGridWidth()
		this.notifySizeChanged()
	}

	initGridWidth () {
		const allCols = this.allCols
		calcActualColWidth(this.ctx, allCols, this.defaultColWidth, this.defaultColMinWidth)
		this.recalcGridWidth()
	}

	recalcGridWidth () {
		this.#width = this.allCols.reduce((acc, cur) => {
			return acc + cur.actualWidth
		}, 0)
	}

	/**
	 * グリッドを描画する
	 * @param {Rect} rect
	 */
	render (rect) {
		if (this.lastRenderedRect && this.lastRenderedRect.includes(rect)) {
			// // 差分更新
			// // しようと思ったが実装コストに見合わないので全描画
			// const moveY = rect.top - this.lastRenderedRect.top
			// const moveX = rect.left - this.lastRenderedRect.left
			// this.#ctx.useContext2d(ctx2d => {
			// 	ctx2d.drawImage(this.#ctx.canvas, -moveX, -moveY)
			// })
			this.renderAll(rect)
		} else {
			this.renderAll(rect)
		}
		this.lastRenderedRect = rect
	}

	/**
	 * グリッドを前回描画した範囲で再描画する
	 */
	rerender () {
		if (this.lastRenderedRect) this.renderAll(this.lastRenderedRect)
	}

	#rerenderRequested = false
	requestRerender () {
		if (this.#rerenderRequested) {
			return
		}
		this.#rerenderRequested = true
		requestAnimationFrame(() => {
			this.rerender()
			this.#rerenderRequested = false
		})
	}

	/**
	 *
	 * @param {Rect} rect
	 */
	renderAll (rect) {
		this.ctx.useContext2d(ctx2d => {
			ctx2d.strokeStyle = this.ctx.gridStyle.borderColor
			ctx2d.fillStyle = this.ctx.gridStyle.gridBgColor
			this.ctx.strokeRect(0, 0, rect.width, rect.height)
			this.ctx.fillRect(0, 0, rect.width, rect.height)
		})
		this.renderRows(rect)
		this.selectBorderRenderer.afterAllRowRendered(rect)
		this.renderColHeader(rect)
	}

	forEachRow (rect, callback) {
		const { firstRenderRowIdx, firstOffsetTop, renderRowCount } = this.calcRowRenderInfo(rect)
		let offsetTop = firstOffsetTop

		let lastIndex = firstRenderRowIdx + renderRowCount
		if (lastIndex >= this.rows.length) {
			lastIndex = this.rows.length - 1
		}

		for (let renderIdx = firstRenderRowIdx; renderIdx <= lastIndex; renderIdx++) {
			callback(this.rows[renderIdx], {
				rowIndex: renderIdx,
				offsetTop,
				isLastRow: renderIdx === lastIndex
			})
			offsetTop += this.rowHeight
		}
	}

	/**
	 *
	 * @param {Rect} rect
	 */
	renderRows (rect) {
		this.forEachRow(rect, (row, {
			rowIndex, offsetTop, isLastRow
		}) => {
			// 非固定セルを最初に描画
			row.renderCells(this.#ctx, {
				offsetTop,
				offsetLeft: -rect.left,
				height: this.rowHeight,
				fixedCols: this.fixedCols,
				cols: this.cols,
				rowIndex: rowIndex,
				currentCellInfo: this.currentCellInfo,
				isLastRow: isLastRow,
				cellInfos: this.cellInfoMap[row.id] || {}
			})
		})

		// 選択モードがセルかつ、選択セルが非固定列なら、選択範囲を描画
		const currentCellInfo = this.ctx.currentCellInfo
		if (currentCellInfo && !currentCellInfo.col.fixed) {
			this.selectBorderRenderer.afterRowRendered(rect)
		}

		this.forEachRow(rect, (row, {
			rowIndex, offsetTop, isLastRow
		}) => {
			// 固定列のセルを描画
			row.renderFixedCells(this.#ctx, {
				offsetTop,
				offsetLeft: -rect.left,
				height: this.rowHeight,
				fixedCols: this.fixedCols,
				cols: this.cols,
				rowIndex: rowIndex,
				currentCellInfo: this.currentCellInfo,
				isLastRow: isLastRow,
				cellInfos: this.cellInfoMap[row.id] || {}
			})
		})

		// 選択セルが固定列なら、選択範囲を描画
		if (currentCellInfo && currentCellInfo.col.fixed) {
			this.selectBorderRenderer.afterRowRendered(rect)
		}
	}

	/**
	 *
	 * @param {Rect} rect
	 */
	calcRowRenderInfo (rect) {
		const firstRenderRowIdx = Math.floor(rect.top / this.rowHeight)
		const firstOffsetTop = rect.top % this.rowHeight * -1 + this.headerHeight
		const renderRowCount = Math.ceil(rect.height / this.rowHeight) + 1
		return {
			firstRenderRowIdx, // 描画範囲内の一番上の行のインデックス
			firstOffsetTop, // 描画範囲内の一番上の行のオフセット
			renderRowCount // 描画範囲内の一番上の行から、描画すべき行数（これ以上描画しても、描画範囲外になる）
		}
	}

	/**
	 *
	 * @param {Rect} rect
	 */
	renderColHeader (rect) {
		const totalFixeCollWidth = this.fixedCols.reduce((totalWidth, col) => {
			return totalWidth + col.actualWidth
		}, 0)

		// 固定じゃない列をまず描画する（固定列にかぶさるところを、固定列描画で上書きするため）
		let offsetLeft = totalFixeCollWidth - rect.left
		this.colsForHeader.forEach(col => {
			col.renderHeaderCell(this.#ctx, { offsetLeft, height: this.headerHeight })
			offsetLeft += col.actualWidth
		})

		// 固定列を描画
		let offsetLeftForFixedCol = 0
		this.fixedColsForHeader.forEach(col => {
			col.renderHeaderCell(this.ctx, { offsetLeft: offsetLeftForFixedCol, height: this.headerHeight })
			offsetLeftForFixedCol += col.actualWidth
		})
	}

	/**
	 *
	 * @param {object} record
	 * @param {object} userData
	 */
	addRow (record) {
		this.modify(() => {
			this.#addRowCore(record)
		})
	}

	addRows (records) {
		this.modify(() => {
			records.forEach((record) => {
				this.#addRowCore(record)
			})
			if (this.autoInsert) {
				this.#addRowCore(this.autoInsert())
			}
		})
	}

	#addRowCore (record) {
		// const colIdToCellMap = Object.entries(record).reduce((map, [key, value]) => {
		// 	const col = this.idToColMap[key]
		// 	if (!col) {
		// 		throw new Error(`unexpected column id. ${key}`)
		// 	}
		// 	map[key] = new Cell(col, value)
		// 	return map
		// }, {})

		// 数万、数十万件のデータを表示する場合、Cellオブジェクトの生成だけで数秒かかってしまうため、
		// Cellオブジェクトの生成は行わず、引数で受け取ったオブジェクトをそのまま保持する。
		const row = new Row(record, record[this.rowIdField])
		this.originalRows.push(row)
		this.idToRowMap[row.id] = row
	}

	deleteRowByIndex (index) {
		this.modify(() => {
			this.#deleteRowCore(index)
		})
	}

	deleteRowById (id) {
		const row = this.idToRowMap[id]
		this.modify(() => {
			this.#deleteRowCore(this.rows.indexOf(row))
		})
	}

	deleteAllRow () {
		this.currentCellRect = null
		this.clearInput()
		this.ctx.currentCellInfo = null
		this.#modifiedRowIds = new Set()
		this.selectBorderRenderer.clear()
		// resetDefaultId()
		this.modify(() => {
			this.ctx.rows = []
			this.originalRows = []
			this.idToRowMap = {}
		})
	}

	#deleteRowCore (index) {
		// 自動生成行は消さない
		if (this.autoInsert && index === this.rows.length - 1) {
			return
		}
		this.modify(() => {
			const deleteRow = this.rows[index]
			delete this.idToRowMap[deleteRow.id]
			const _index = this.originalRows.findIndex(row => row.id === deleteRow.id)
			this.originalRows.splice(_index, 1)
		})
	}

	selectRowByIndex (rowIndex) {
		const updated = this.selectBorderRenderer.select(rowIndex, null, {
			shiftKey: false,
			ctrlKey: false
		})
		if (updated) this.requestRerender()
	}

	selectRowById (id) {
		const row = this.idToRowMap[id]
		const updated = this.selectBorderRenderer.select(this.rows.indexOf(row), null, {
			shiftKey: false,
			ctrlKey: false
		})
		if (updated) this.requestRerender()
	}

	clearSelect () {
		this.currentCellRect = null
		this.clearInput()
		this.ctx.currentCellInfo = null
		this.selectBorderRenderer.clear()
		this.requestRerender()
	}

	findRows (predicate) {
		return this.rows.map(row => new PublicRow(this, row)).filter(predicate)
	}

	findOriginalRows (predicate) {
		return this.originalRows.map(row => new PublicRow(this, row)).filter(predicate)
	}

	getCell (rowIndex, colId) {
		const row = this.rows[rowIndex]
		const col = this.idToColMap[colId]
		const grid = this
		return new Cell(grid, row, col)
	}

	getCellById (rowId, colId) {
		const row = this.idToRowMap[rowId]
		const col = this.idToColMap[colId]
		const grid = this
		return new Cell(grid, row, col)
	}

	getCurrentCell () {
		if (!this.currentCellInfo) return
		const row = this.rows[this.currentCellInfo.rowIndex]
		const col = this.currentCellInfo.col
		return new Cell(this, row, col)
	}

	getSelectedRows () {
		if (this.#ctx.selectMode === "multiRow" ||
			this.#ctx.selectMode === "row" ||
			this.#ctx.selectMode === "boundary") {
			return this.selectBorderRenderer.getSelectedIndice().map(index => {
				return new PublicRow(this, this.rows[index])
			})
		}
		if (this.currentCellInfo) {
			return [new PublicRow(this, this.rows[this.currentCellInfo.rowIndex])]
		}
		return []
	}

	getModifiedRows () {
		return this.rows
			.filter(row => this.#modifiedRowIds.has(row.id))
			.map(row => new PublicRow(this, row))
	}

	/**
	 * 選択されている領域の値を2重配列で取得
	 * @returns
	 */
	getSelectedBoundsData () {
		if (this.#ctx.selectMode === "boundary") {
			const bound = this.selectBorderRenderer.bound
			const allCols = this.allCols
			const selectedCols = allCols.slice(allCols.findIndex(col => col.id === bound.fromColumn.id), allCols.findIndex(col => col.id === bound.toColumn.id) + 1)
			return this.rows.slice(bound.fromRowIndex, bound.toRowIndex + 1).map(row => {
				const data = row.data
				return selectedCols.reduce((newRow, col) => {
					newRow.push(data[col.id])
					return newRow
				}, [])
			})
		}
		if (this.#ctx.selectMode === "multiRow" || this.#ctx.selectMode === "row") {
			// 行選択モードの時のみ、非表示列もコピペできるようにする
			const allColIds = this.allColsWithUnvisible.map(col => col.id)
			return this.getSelectedRows().map(row => allColIds.reduce((newRow, key) => {
				newRow.push(row.data[key])
				return newRow
			}, []))
		}
		return [[this.currentCellInfo.value]]
	}

	moveRowFromTo (fromIndex, toIndex) {
		if (fromIndex < toIndex) {
			toIndex--
		}
		const temp = this.rows[fromIndex]
		this.rows.splice(fromIndex, 1)
		this.rows.splice(toIndex, 0, temp)
	}

	/**
	 *
	 * @param {MouseEvent} e
	 */
	clickAt (e) {
		const y = e.offsetY
		const x = e.offsetX
		if (y < this.headerHeight) {
			// ヘッダが選択された場合の処理
			// マウスがのっかっているカラムを取得
			const { col, offsetLeft } = this.findColByX(x)

			// TODO このあたりのロジックはカラム側にあったほうがいいかもしれない
			// ヘッダカラムのソートやフィルターが選択された場合の処理
			if (col) {
				const colSizeChangeBoundary = offsetLeft + col.actualWidth - 4
				const sortBoundary = colSizeChangeBoundary - (col.sortable ? col.sortIconSize + this.ctx.cellHPadding : 0)
				const filterBoundary = sortBoundary - (col.filterable ? col.filterIconSize + this.ctx.cellHPadding * 2 : 0)
				if (x > colSizeChangeBoundary) {
				} else if (col.sortable && x <= colSizeChangeBoundary && x > sortBoundary) {
					this.changeSort(col.sortTargetCol || col.id)
				} else if (col.filterable && x <= sortBoundary && x > filterBoundary) {
					this.onFilterClicked(offsetLeft, col)
				}
			}
			return
		}

		const {
			col,
			rowIdx,
			offsetTop,
			offsetLeft,
			fixeColWidth
		} = this.getRowColAt(x, y)
		if (rowIdx != null && !this.rows[rowIdx]) {
			this.currentRow = null
			this.ctx.currentCellInfo = null
			this.currentCellRect = null
			// this.renderAll(this.lastRenderedRect) // 呼び出し側で再レンダリングすることを期待する。
			this.ctx.dispatchEvent(new CustomEvent("unselect", { detail: { cell: new Cell(this, this.currentRow, col) } }))
			this.selectBorderRenderer.clear()
			return
		}

		this.selectBorderRenderer.select(rowIdx, col, e)

		if (col) {
			// 行ヘッダの場合は何もしない -> 選択するよう指示あったので修正
			// if (col.type === "row-header") {
			// 	return
			// }
			this.setCurrentCellInfo({ rowIndex: rowIdx, col, top: offsetTop, left: offsetLeft, fixedColWidth: fixeColWidth })
			this.ctx.dispatchEvent(new CustomEvent("cell-clicked", { detail: { cell: new Cell(this, this.currentRow, col) } }))
			// this.renderAll(this.lastRenderedRect) // 呼び出し側で再レンダリングすることを期待する。
		}
	}

	rightClickAt (x, y) {
		if (y < this.headerHeight) {
			return
		}

		const {
			col,
			rowIdx
		} = this.getRowColAt(x, y)
		if (col) {
			const row = this.rows[rowIdx]
			// this.createInput()
			this.ctx.dispatchEvent(new CustomEvent("cell-rightclicked", { detail: { cell: new Cell(this, row, col) } }))
		}
	}

	getRowColAt (x, y) {
		if (y < this.headerHeight) {
			return {
				rowIdx: -1
			}
		}

		// 行
		const { rowIdx, offsetTop } = this.#findVisibleRow((offsetTop, rowIndex) => {
			return !(y < offsetTop || offsetTop + this.rowHeight < y)
		})
		if (rowIdx !== -1) {
			// 列
			const { offsetLeft, fixeColWidth, col } = this.findColByX(x)
			return {
				col,
				rowIdx,
				offsetTop,
				offsetLeft,
				fixeColWidth
			}
		}
		return {
			rowIdx: -1
		}
	}

	setCurrentCellInfo ({ rowIndex, col, top, left, fixedColWidth }) {
		this.currentCol = col
		const row = this.currentRow = this.rows[rowIndex]

		if (!row || !col) {
			this.ctx.currentCellInfo = null
			this.currentCellRect = null
			return
		}

		// 左にはみ出す場合
		if (!col.options.fixed && fixedColWidth > left) {
			this.requestScroll(left - fixedColWidth, 0)
		}

		const visibleBottom = this.lastRenderedRect.top + this.lastRenderedRect.height
		const visibleTop = this.headerHeight + this.lastRenderedRect.top
		if (top + this.rowHeight > visibleBottom) {
			// 下にはみ出す場合
			this.requestScroll(0, top + this.rowHeight - visibleBottom)
		} else if (visibleTop > top) {
			// 上にはみ出す場合
			this.requestScroll(0, top - visibleTop)
		}

		this.currentCellRect = new Rect({
			top: top,
			left: this.lastRenderedRect.left + left,
			height: this.rowHeight,
			width: col.actualWidth
		})

		this.ctx.currentCellInfo = {
			col,
			rowIndex: rowIndex,
			rowId: row.id,
			row: row,
			get value () {
				return row.data[col.id]
			}
		}
		this.createInput()

		// カレントセルが変わったイベントを発火
		this.ctx.dispatchEvent(new CustomEvent("cell-selected", {
			detail: {
				cell: this.ctx.currentCellInfo,
				rect: this.ctx.currentCellRect
			}
		}))
	}

	// #region ドラッグ関連
	#dragStartX = -1
	#draggingCol
	#dragStartWidth

	#dragStartRowIndex = -1

	get isHeaderDragging () {
		return this.#dragStartX >= 0
	}

	get isRowDragging () {
		return this.#dragStartRowIndex >= 0
	}

	dragStart (x, y) {
		if (y >= this.headerHeight) {
			if (!this.currentCellInfo) return

			const { rowIdx } = this.getRowColAt(x, y)
			if (this.currentCellInfo.rowIndex === rowIdx) {
				this.#dragStartRowIndex = rowIdx
			}
		} else	if (this.#draggingCol != null) {
			this.#dragStartX = x
			this.#dragStartWidth = this.#draggingCol.actualWidth
		}
	}

	dragEnd () {
		this.#dragStartX = -1
		this.#dragStartRowIndex = -1
		this.#draggingCol = null
	}

	// マウスカーソルが指定箇所にある場合の処理
	pointAt (x, y) {
		if (this.isHeaderDragging) {
			const dx = x - this.#dragStartX
			this.#draggingCol.actualWidth = this.#dragStartWidth + dx
			if (this.#draggingCol.actualWidth < 10) this.#draggingCol.actualWidth = 10
			this.renderAll(this.lastRenderedRect)
			this.notifySizeChanged()
			return
		}

		if (this.isRowDragging && (this.ctx.selectMode === "multiRow" || this.ctx.selectMode === "boundary")) {
			const { rowIdx, col } = this.getRowColAt(x, y)
			const shouldUpdate = this.selectBorderRenderer.select(rowIdx, col, { shiftKey: true })
			if (shouldUpdate) {
				this.requestRerender()
			}
			return
		}

		// ヘッダ
		if (y < this.headerHeight) {
			// マウスがのっかっているカラムを取得
			const { col, offsetLeft } = this.findCol((left, col) => {
				return left < x && x <= left + col.actualWidth
			})

			// TODO このあたりのロジックはカラム側にあったほうがいいかもしれない
			if (col) {
				const colSizeChangeBoundary = offsetLeft + col.actualWidth - 4
				const sortBoundary = colSizeChangeBoundary - (col.sortable ? col.sortIconSize + this.ctx.cellHPadding : 0)
				const filterBoundary = sortBoundary - (col.filterable ? col.filterIconSize + this.ctx.cellHPadding * 2 : 0)
				if (x > colSizeChangeBoundary) {
					// 右端ならカラムサイズ変更のドラッグ開始
					this.requestCursorChange("col-resize")
					this.#draggingCol = col
				} else if (col.sortable && x <= colSizeChangeBoundary && x > sortBoundary) {
					this.requestCursorChange("pointer")
				} else if (col.filterable && x <= sortBoundary && x > filterBoundary) {
					this.requestCursorChange("pointer")
				} else {
					this.requestCursorChange("default")
				}
			} else {
				this.requestCursorChange("default")
			}
			return
		}

		// // 行
		const { col } = this.findColByX(x)
		if (col && col.cursor) {
			this.requestCursorChange(col.cursor)
		} else {
			this.requestCursorChange("default")
		}
	}

	// #endregion

	/**
	 *
	 * @param {number} x x方向の増加分
	 * @param {number} y y方向の増加分
	 * @returns
	 */
	moveCurrentCell (x, y) {
		if (!this.ctx.currentCellInfo) return
		const { col, rowIndex } = this.ctx.currentCellInfo

		const allCols = this.allCols
		const colIndex = allCols.indexOf(col)
		const headerColSize = allCols.filter(col => col.type === "row-header").length
		// 上限、下限からはみ出すの場合は何もしない
		if (colIndex + x < headerColSize || colIndex + x >= allCols.length) {
			return
		}
		// 左右にはみ出す場合は何もしない
		if (rowIndex + y < 0 || rowIndex + y >= this.rows.length) {
			return
		}
		const newCol = allCols[colIndex + x]
		const newRowIndex = rowIndex + y

		this.selectBorderRenderer.select(newRowIndex, newCol)

		const { offsetLeft, fixeColWidth } = this.findColById(newCol.id)
		const offsetTop = this.headerHeight + this.rowHeight * newRowIndex

		this.setCurrentCellInfo({ top: offsetTop, left: offsetLeft, rowIndex: newRowIndex, col: newCol, fixedColWidth: fixeColWidth })
		this.renderAll(this.lastRenderedRect)
	}

	findCol (predicate, rect = this.lastRenderedRect) {
		let offsetLeft = 0
		let foundCol
		for (const col of this.fixedCols) {
			if (predicate(offsetLeft, col)) {
				foundCol = col
				break
			} else {
				offsetLeft += col.actualWidth
			}
		}
		const fixeColWidth = offsetLeft
		if (!foundCol) {
			offsetLeft -= rect.left
			for (const col of this.cols) {
				if (predicate(offsetLeft, col)) {
					foundCol = col
					break
				} else {
					offsetLeft += col.actualWidth
				}
			}
		}
		return {
			col: foundCol,
			fixeColWidth,
			offsetLeft
		}
	}

	findColByX (x) {
		return this.findCol((left, col) => {
			return !(x < left || left + col.actualWidth < x)
		})
	}

	findColById (id, rect) {
		return this.findCol((left, col) => {
			return col.id === id
		}, rect)
	}

	#findVisibleRow (predicate) {
		const { firstRenderRowIdx, firstOffsetTop, renderRowCount } = this.calcRowRenderInfo(this.lastRenderedRect)
		let rowIdx = firstRenderRowIdx
		let offsetTop = firstOffsetTop
		const lastIndex = firstRenderRowIdx + renderRowCount
		let found = false
		for (; rowIdx < lastIndex; rowIdx++) {
			if (predicate(offsetTop, rowIdx)) {
				found = true
				break
			}
			offsetTop += this.rowHeight
		}
		return { rowIdx: found ? rowIdx : -1, offsetTop: offsetTop + this.lastRenderedRect.top }
	}

	modify (callback) {
		if (this.#isModifying) return callback()
		this.#isModifying = true
		try {
			return callback()
		} finally {
			this.applySortAndFilter()
			this.notifySizeChanged()
			this.#isModifying = false
		}
	}

	notifySizeChanged () {
		this.recalcGridWidth()
		if (this.onSizeChangedListener) this.onSizeChangedListener()
	}

	#inputCanceller
	createInput () {
		this.clearInput()
		if (!this.currentCellInfo) return
		const isReadOnly = this.#readonlyRowRule(this.currentCellInfo.row.data) || this.currentCellInfo.col.readonly
		if (isReadOnly && !isMobile) {
			// 読み取り専用の場合、要素は生成せず、キー入力受付用の要素にフォーカス
			// ※この時点では、グリッド操作用要素の座標が古い可能性があるので、描画された後にフォーカス
			window.requestAnimationFrame(() => {
				this.ctx.controlEl.focus()
			})
			return
		}
		const currentCellInfo = this.currentCellInfo
		let oldValue = currentCellInfo.value

		this.#inputCanceller = currentCellInfo.col.editCell(
			this.ctx,
			// 値が入力された時
			val => {
				this.modifyCell(currentCellInfo.row, currentCellInfo.col, val)
			},
			// 編集が完了したとき
			() => {
				const row = this.rows[currentCellInfo.rowIndex]
				const col = currentCellInfo.col
				const cell = new Cell(this, row, col)

				// 値が変わっていればcell-editedを発火
				if (cell.value !== oldValue) {
					oldValue = cell.value
					this.ctx.dispatchEvent(new CustomEvent("cell-edited", { detail: { cell } }))
				}
			})
	}

	modifyCell (row, col, value) {
		const cell = new Cell(this, row, col)
		const oldValue = row.data[col.id]
		row.data[col.id] = value
		this.#modifiedRowIds.add(row.id)
		this.requestRerender()

		const lastRow = this.rows[this.rows.length - 1]

		if (
			this.autoInsert &&
			lastRow.id === row.id &&
			cell.value !== oldValue) {
			this.modify(() => {
				this.#addRowCore(this.autoInsert())
			})
		}
	}

	clearInput () {
		this.#ctx.inputEl.innerHTML = ""
	}

	changeSort (id) {
		const col = this.idToColMap[id]
		if (col.sortDir === Column.SORT_DIRS.NONE) {
			this.resetSort()
			this.sort(id, true)
		} else if (col.sortDir === Column.SORT_DIRS.ASC) {
			this.resetSort()
			this.sort(id, false)
		} else if (col.sortDir === Column.SORT_DIRS.DESC) {
			this.resetSort()
			this.applySortAndFilter()
			this.requestRerender()
		}
	}

	sort (id, isAsc) {
		const col = this.idToColMap[id]

		col.sortDir = isAsc ? Column.SORT_DIRS.ASC : Column.SORT_DIRS.DESC
		this.sortedCol = col

		this.#sorters.push(col.sort)

		this.applySortAndFilter()
		this.requestRerender()
	}

	resetSort () {
		if (this.sortedCol) {
			this.sortedCol.sortDir = Column.SORT_DIRS.NONE
			this.sortedCol = null
			this.#sorters = []
		}
	}

	#sorters = []
	#filters = []

	applySortAndFilter () {
		const originalRows = [...this.originalRows]
		let autoInsertRow
		if (this.autoInsert) {
			autoInsertRow = originalRows.pop()
		}
		let filtered = this.#filters.reduce((rows, cur) => rows.filter(row => cur(row.data)), originalRows)
		filtered = this.allCols.reduce((rows, col) => {
			if (!col.isFiltered) return rows
			return rows.filter(row => {
				const value = row.data[col.id]
				if (value == null) return false
				return col.filteredValues.some(filteredValue => filteredValue === value)
			})
		}, filtered)
		const movedRows = this.#sorters.reduce((rows, cur) => rows.sort(cur), filtered)
		if (autoInsertRow) {
			movedRows.push(autoInsertRow)
		}
		this.ctx.rows = movedRows
	}

	addFilter (filter) {
		this.modify(() => {
			this.#filters.push(filter)
		})
	}

	removeFilter () {
		this.modify(() => {
			this.#filters = []
		})
	}

	/**
	 *
	 * @param {string} rowId
	 * @param {string[]} errorColIds
	 */
	addError (rowId, errorColIds) {
		errorColIds.forEach(colId => {
			let colInfo = this.cellInfoMap[rowId]
			if (!colInfo) {
				colInfo = {}
				this.cellInfoMap[rowId] = colInfo
			}
			colInfo[colId] = {
				isError: true,
				bgColor: this.#ctx.gridStyle.errorBgColor
			}
		})
	}

	/**
	 *
	 * @param {Object.<string, string[]>} errors
	 */
	addErrors (errors) {
		Object.entries(errors).forEach(([rowId, colIds]) => {
			this.addError(rowId, colIds)
		})
		this.requestRerender()
	}

	/**
	 */
	addErrorByIndex (index, colId, errors) {
		const row = this.rows[index]
		if (!row) return
		this.addError(row.id, colId)
		this.requestRerender()
	}

	clearErrors () {
		Object.values(this.cellInfoMap).forEach(colInfos => {
			Object.keys(colInfos).forEach(colId => {
				const cellInfo = colInfos[colId]
				if (cellInfo && cellInfo.isError) {
					delete colInfos[colId]
				}
			})
		})
		this.requestRerender()
	}

	/**
	 *
	 * @param {string} rowId
	 * @param {string} colId
	 * @param {{color:string, bgColor:string}} color
	 */
	setColor (rowId, colId, color = {}) {
		let rowInfo = this.cellInfoMap[rowId]
		if (!rowInfo) {
			rowInfo = {}
			this.cellInfoMap[rowId] = rowInfo
		}
		rowInfo[colId] = color
		this.requestRerender()
	}

	setRowColor (rowId, color) {
		const allCols = this.allCols
		allCols.forEach(col => {
			this.setColor(rowId, col.id, color)
		})
	}

	clearColors () {
		this.cellInfoMap = {}
		this.requestRerender()
	}

	setReadonlyRowRule (rule) {
		this.#readonlyRowRule = rule
		this.requestRerender()
	}

	copy () {
		navigator.clipboard.writeText(JSON.stringify(this.getSelectedBoundsData()))
	}

	paste () {
		// グリッドが選択されていなければ何もしない
		if (!this.currentCellInfo) return

		const { rowIndex, col } = this.currentCellInfo

		const isSelectModeRow = this.#ctx.selectMode === "row" || this.#ctx.selectMode === "multiRow"
		// 行選択モードの時のみ、非表示列もコピペできるようにする
		const allCols = isSelectModeRow ? this.allColsWithUnvisible : this.allCols
		const allRows = this.rows

		// ペースト中フラグON
		this.#ctx.isPasting = true
		navigator.clipboard.readText().then(jsonStr => {
			if (!jsonStr) {
				// ペースト中フラグOFF
				this.#ctx.isPasting = false
				return
			}
			let data = []
			try {
				data = JSON.parse(jsonStr)
			} catch (error) {
				// セル以外からのコピペ
				data.push([jsonStr])
			}
			if (Array.isArray(data)) {
				const rowIndice = []
				const cells = []
				const bound = this.selectBorderRenderer.bound
				if (data.length === 1 && data[0].length === 1 && bound) {
					// boundaryかつコピーセルが1つ
					const selectedCols = allCols.slice(allCols.findIndex(col => col.id === bound.fromColumn.id), allCols.findIndex(col => col.id === bound.toColumn.id) + 1)
					for (let rowIdx = bound.fromRowIndex; rowIdx <= bound.toRowIndex; rowIdx++) {
						// ペースト対象がreadonly行であればスキップ
						if (this.#readonlyRowRule(this.rows[rowIdx].data)) continue
						for (let colIdx = 0; colIdx < selectedCols.length; colIdx++) {
							if (!selectedCols[colIdx].readonly) {
								const cell = new Cell(this, this.rows[rowIdx], col)
								cells.push(cell)
								this.rows[rowIdx].data[selectedCols[colIdx].id] = data[0][0]
								this.ctx.dispatchEvent(new CustomEvent("cell-edited", { detail: { cell: cell } }))
							}
						}
						rowIndice.push(rowIdx)
						this.#modifiedRowIds.add(this.rows[rowIdx].id)
					}
				} else {
					// boundaryかつコピーセルが1つ以上、またはboundary以外
					for (let ri = 0; ri < data.length && ri < allRows.length; ri++) {
						const index = rowIndex + ri
						rowIndice.push(index)
						const row = this.rows[index]
						const rowData = data[ri]
						let updatingCol = col
						if (isSelectModeRow) {
						// 先頭列から最終列までをペーストの対象としたい
							updatingCol = allCols[0]
						}

						// ペースト対象がreadonly行であればスキップ
						if (row) {
							if (this.#readonlyRowRule(row.data)) continue
						}

						for (let i = 0; i < rowData.length && updatingCol; i++) {
							if (!updatingCol.readonly && row) {
								row.data[updatingCol.id] = rowData[i]
							}
							updatingCol = allCols[allCols.indexOf(updatingCol) + 1]
						}
						this.#modifiedRowIds.add(row.id + ri)
						cells.push(new Cell(this, row, col))
					}
				}
				this.ctx.dispatchEvent(new CustomEvent("cell-paste", { detail: { rowIndice, cells } }))
				this.requestRerender()
			}
			// ペースト中フラグOFF
			this.#ctx.isPasting = false
		})
	}

	deleteSelectedBoundaryValue () {
		if (this.#ctx.selectMode !== "boundary") return

		const bound = this.selectBorderRenderer.bound
		if (!bound) return

		const deleteTargetCols = []
		let isTarget = false
		this.allCols.forEach(col => {
			if (col.id === bound.fromColumn.id) {
				isTarget = true
			}

			if (isTarget && !col.readonly && !col.hidden) {
				deleteTargetCols.push(col)
			}

			if (col.id === bound.toColumn.id) {
				isTarget = false
			}
		})

		this.getSelectedRows().forEach(row => {
			if (this.#readonlyRowRule(row.data)) return
			deleteTargetCols.forEach(col => {
				const cell = row.getCellByColId(col.id)
				if (col.options.type === undefined || col.options.type === "text" || col.options.type === "textarea") {
					cell.value = ""
				} else {
					cell.value = null
				}
				// 編集イベント発火
				this.ctx.dispatchEvent(new CustomEvent("cell-edited", { detail: { cell: cell } }))
			})
		})
	}
}
